// src/redux/slices/subtasksSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Subtask, SubtaskStatus } from '../../models/Subtask';
import { BreakdownProblem } from '../../services/api';
import { collection, query, where, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import { AppDispatch } from '../store'; // Make sure to import AppDispatch

interface SubtasksState {
  subtasks: Subtask[];
  loading: boolean;
  error: string | null;
  generatingSubtasks: boolean;
}

const initialState: SubtasksState = {
  subtasks: [],
  loading: false,
  error: null,
  generatingSubtasks: false,
};

// Async thunk for generating subtasks
export const generateSubtasks = createAsyncThunk<
  void,
  { problemId: string; userId: string },
  { rejectValue: string }
>('subtasks/generate', async ({ problemId, userId }, { rejectWithValue }) => {
  try {
    const response = await BreakdownProblem({ problemId, userId });
    return response;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to generate subtasks');
  }
});

// Async thunk for updating subtask status
export const updateSubtaskStatus = createAsyncThunk<
  { subtaskId: string; status: SubtaskStatus },
  { subtaskId: string; status: SubtaskStatus },
  { rejectValue: string }
>('subtasks/updateStatus', async ({ subtaskId, status }, { rejectWithValue }) => {
  try {
    const subtaskRef = doc(db, 'subtasks', subtaskId);
    await updateDoc(subtaskRef, {
      status,
      updatedAt: new Date().toISOString(),
    });
    return { subtaskId, status };
  } catch (error: any) {
    return rejectWithValue('Failed to update subtask status');
  }
});

const subtasksSlice = createSlice({
  name: 'subtasks',
  initialState,
  reducers: {
    setSubtasks: (state, action: PayloadAction<Subtask[]>) => {
      state.subtasks = action.payload;
    },
    clearSubtasks: state => {
      state.subtasks = [];
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(generateSubtasks.pending, state => {
        state.generatingSubtasks = true;
        state.error = null;
      })
      .addCase(generateSubtasks.fulfilled, state => {
        state.generatingSubtasks = false;
      })
      .addCase(generateSubtasks.rejected, (state, action) => {
        state.generatingSubtasks = false;
        state.error = action.payload as string;
      })
      .addCase(updateSubtaskStatus.fulfilled, (state, action) => {
        const { subtaskId, status } = action.payload;
        const subtask = state.subtasks.find(s => s.id === subtaskId);
        if (subtask) {
          subtask.status = status;
          subtask.updatedAt = new Date().toISOString();
        }
      });
  },
});

export const { setSubtasks, clearSubtasks, setError } = subtasksSlice.actions;

export default subtasksSlice.reducer;
